import { createTheme } from '@mui/material/styles';

// Enterprise Color System
const colors = {
  // Primary Enterprise Colors (Slate Scale)
  slate: {
    900: '#0F172A',  // Deep navy - primary background
    800: '#1E293B',  // Charcoal - secondary background  
    700: '#334155',  // Steel blue - elevated surfaces
    600: '#475569',  // Medium gray - borders
    500: '#64748B',  // Cool gray - disabled text
    400: '#94A3B8',  // Light gray - secondary text
    300: '#CBD5E1',  // Border gray - dividers
    200: '#E2E8F0',  // Very light gray - subtle backgrounds
    100: '#F1F5F9',  // Off white - card backgrounds
    50: '#F8FAFC',   // Pure light - text on dark
  },
  
  // Corporate Blue (Primary Actions)
  blue: {
    700: '#1D4ED8',  // Pressed state
    600: '#2563EB',  // Primary
    500: '#3B82F6',  // Hover state
  },
  
  // Semantic Colors
  emerald: {
    600: '#059669',  // Success
    500: '#10B981',  // Success hover
  },
  amber: {
    600: '#D97706',  // Warning
    500: '#F59E0B',  // Warning hover
  },
  red: {
    600: '#DC2626',  // Error
    500: '#EF4444',  // Error hover
  },
  
  white: '#FFFFFF',
  
  // Legacy mappings for backward compatibility
  navy: '#0F172A',
  charcoal: '#1E293B',
  offwhite: '#F8FAFC',
  paleGray: '#94A3B8',
  coastalBlue: '#2563EB',
  lightBlue: '#3B82F6',
  sage: '#059669',
  sand: '#E2E8F0',
  seafoam: '#CBD5E1'
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.blue[600],      // Corporate blue
      light: colors.blue[500],     // Hover state
      dark: colors.blue[700],      // Pressed state
      contrastText: colors.white,
    },
    secondary: {
      main: colors.slate[300],     // Neutral secondary
      light: colors.slate[200],    // Light secondary
      dark: colors.slate[400],     // Dark secondary
      contrastText: colors.slate[900],
    },
    background: {
      default: colors.slate[900],  // Deep navy background
      paper: colors.slate[800],    // Card/paper background
    },
    surface: {
      main: colors.slate[700],     // Elevated surfaces
      light: colors.slate[600],    // Light elevated
      dark: colors.slate[800],     // Dark elevated
    },
    text: {
      primary: colors.slate[50],   // Primary text (white)
      secondary: colors.slate[400], // Secondary text (light gray)
      disabled: colors.slate[500], // Disabled text
    },
    divider: colors.slate[600],    // Divider lines
    
    // Semantic colors
    success: {
      main: colors.emerald[600],
      light: colors.emerald[500],
      contrastText: colors.white,
    },
    warning: {
      main: colors.amber[600],
      light: colors.amber[500],
      contrastText: colors.white,
    },
    error: {
      main: colors.red[600],
      light: colors.red[500],
      contrastText: colors.white,
    },
    info: {
      main: colors.blue[600],
      light: colors.blue[500],
      contrastText: colors.white,
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    // Heading styles with proper hierarchy
    h1: { 
      fontSize: '2rem',      // 32px
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
      color: colors.slate[50]
    },
    h2: { 
      fontSize: '1.5rem',    // 24px
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
      color: colors.slate[50]
    },
    h3: { 
      fontSize: '1.25rem',   // 20px
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
      color: colors.slate[50]
    },
    h4: { 
      fontSize: '1.125rem',  // 18px
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.slate[50]
    },
    h5: { 
      fontSize: '1rem',      // 16px
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.slate[50]
    },
    h6: { 
      fontSize: '0.875rem',  // 14px
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.slate[50]
    },
    // Body text styles
    body1: {
      fontSize: '1rem',      // 16px
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.slate[50]
    },
    body2: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.slate[400]
    },
    // UI element styles
    button: {
      fontSize: '0.875rem',  // 14px
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
      letterSpacing: '0.025em'
    },
    caption: {
      fontSize: '0.75rem',   // 12px
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.slate[400]
    },
    overline: {
      fontSize: '0.75rem',   // 12px
      fontWeight: 600,
      lineHeight: 1.5,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: colors.slate[400]
    },
    // Custom variant for monospace text (coordinates, IDs, etc.)
    monospace: {
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Monaco', 'Consolas', monospace",
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.slate[300]
    }
  },
  components: {
    // Typography component overrides
    MuiTypography: {
      styleOverrides: {
        root: {
          // Ensure consistent font smoothing
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          minHeight: 48,
          padding: '12px 24px',
          color: colors.slate[400],
          backgroundColor: colors.slate[700],
          border: `1px solid ${colors.slate[600]}`,
          borderRadius: '6px 6px 0 0',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: colors.slate[600],
            color: colors.slate[200],
          },
          '&.Mui-selected': {
            backgroundColor: colors.slate[100],
            color: colors.slate[900],
            fontWeight: 600,
            borderColor: colors.slate[300],
            '&:hover': {
              backgroundColor: colors.slate[50],
              color: colors.slate[900],
            }
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: {
          display: 'none', // Hide the default indicator since we're using background colors
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: 1.5,
          textTransform: 'none',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }
        },
        containedPrimary: {
          backgroundColor: colors.blue[600],
          color: colors.white,
          '&:hover': {
            backgroundColor: colors.blue[500],
          },
          '&:active': {
            backgroundColor: colors.blue[700],
          }
        },
        containedSecondary: {
          backgroundColor: colors.slate[600],
          color: colors.slate[50],
          '&:hover': {
            backgroundColor: colors.slate[500],
          },
          '&:active': {
            backgroundColor: colors.slate[700],
          }
        },
        outlined: {
          borderColor: colors.slate[600],
          color: colors.slate[50],
          '&:hover': {
            borderColor: colors.slate[500],
            backgroundColor: colors.slate[800],
          }
        }
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#2D3436',
          border: `1px solid ${colors.slate[700]}`,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        },
        elevation2: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
        elevation3: {
          boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.slate[800],
          border: `1px solid ${colors.slate[700]}`,
          borderRadius: 8,
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.slate[600],
        }
      }
    }
  },
});

// Add custom typography variant to theme
theme.typography.monospace = {
  fontFamily: "'JetBrains Mono', 'Fira Code', 'Monaco', 'Consolas', monospace",
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.5,
  color: colors.slate[300]
};

export default theme; 